import { Component, OnInit } from '@angular/core';
import { MatCard, MatCardContent } from "@angular/material/card";
import { TranslateModule } from "@ngx-translate/core";
import { MatError, MatFormField, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/core";
import { MatSelect } from "@angular/material/select";
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppUrlConstants } from '../../core/constants/AppUrlConstants';
import { GenericCrudService } from '../../core/services/generic-crud.service';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ApplicationDataService } from '../../core/services/application-data.service';

@Component({
  selector: 'app-group-users',
  standalone: true,
  imports: [
    MatCard, MatCardContent, TranslateModule, MatError, MatFormField,
    MatLabel, MatOption, MatSelect, ReactiveFormsModule, FormsModule
  ],
  templateUrl: './group-users.component.html',
  styleUrls: ['./group-users.component.css']
})
export class GroupUsersComponent implements OnInit {

  form: FormGroup;
  usersList: any[] = [];
  selectedUsersList: any[] = [];
  displayUsersList: any[] = [];
  groupsList: any[] = [];
  filteredgroupsList: any[] = [];

  hasUnsavedChanges = false;
  availableSearch = '';
  assignedSearch = '';

  // Checkbox tracking
  checkedAvailableIds: any[] = [];
  checkedAssignedIds: any[] = [];

  constructor(
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private crudService: GenericCrudService,
    private appDataService: ApplicationDataService
  ) {}

  ngOnInit() {
    this.form = this.formBuilder.group({
      groupId: [null, [Validators.required]],
      user: [null],
      userIds: [null]
    });
    this.getAllGroups();
    this.getUsers();

    this.f['groupId'].valueChanges.subscribe((value) => {
      if (value) {
        this.appDataService.getGroupById(value).subscribe({
          next: (res: any) => {
            if (res?.data?.users) {
              this.selectedUsersList = res?.data?.users;
              this.f['userIds'].setValue(this.getIds(this.selectedUsersList));
            } else {
              this.selectedUsersList = [];
              this.f['userIds'].setValue([]);
            }
            this.displayUsers();
            this.hasUnsavedChanges = false;
            this.checkedAvailableIds = [];
            this.checkedAssignedIds = [];
          }
        });
      }
    });
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  filterBySearch(list: any[], search: string): any[] {
    if (!search || !list) return list || [];
    return list.filter(item => item.userName?.toLowerCase().includes(search.toLowerCase()));
  }

  // --- Checkbox methods ---

  isCheckedAvailable(user: any): boolean {
    return this.checkedAvailableIds.includes(user.userId);
  }

  isCheckedAssigned(user: any): boolean {
    return this.checkedAssignedIds.includes(user.userId);
  }

  toggleAvailableCheck(user: any): void {
    const idx = this.checkedAvailableIds.indexOf(user.userId);
    if (idx >= 0) {
      this.checkedAvailableIds.splice(idx, 1);
    } else {
      this.checkedAvailableIds.push(user.userId);
    }
  }

  toggleAssignedCheck(user: any): void {
    const idx = this.checkedAssignedIds.indexOf(user.userId);
    if (idx >= 0) {
      this.checkedAssignedIds.splice(idx, 1);
    } else {
      this.checkedAssignedIds.push(user.userId);
    }
  }

  // --- Move methods (arrow buttons) ---

  moveToAssigned(): void {
    if (this.checkedAvailableIds.length === 0) return;
    const items = this.displayUsersList.filter(u => this.checkedAvailableIds.includes(u.userId));
    this.selectedUsersList.push(...items);
    this.f['userIds'].setValue(this.getIds(this.selectedUsersList));
    this.checkedAvailableIds = [];
    this.displayUsers();
    this.hasUnsavedChanges = true;
  }

  moveToAvailable(): void {
    if (this.checkedAssignedIds.length === 0) return;
    this.selectedUsersList = this.selectedUsersList.filter(u => !this.checkedAssignedIds.includes(u.userId));
    this.f['userIds'].setValue(this.getIds(this.selectedUsersList));
    this.checkedAssignedIds = [];
    this.displayUsers();
    this.hasUnsavedChanges = true;
  }

  // --- Existing methods ---

  selectAll() {
    this.selectedUsersList = [...this.usersList];
    this.f['userIds'].setValue(this.getIds(this.selectedUsersList));
    this.checkedAvailableIds = [];
    this.displayUsers();
    this.hasUnsavedChanges = true;
  }

  removeAll() {
    this.selectedUsersList = [];
    this.f['userIds'].setValue([]);
    this.checkedAssignedIds = [];
    this.displayUsers();
    this.hasUnsavedChanges = true;
  }

  updateUsers() {
    this.crudService.edit(this.form.value, AppUrlConstants.GROUP_URL + AppUrlConstants.ADD_USERS).subscribe({
      next: (res: any) => {
        if (res?.data?.users) {
          this.selectedUsersList = res?.data?.users;
          this.f['userIds'].setValue(this.getIds(this.selectedUsersList));
          this.displayUsers();
        }
        this.hasUnsavedChanges = false;
        this.checkedAvailableIds = [];
        this.checkedAssignedIds = [];
        this.openAlert('Users updated successfully');
      },
      error: (e) => {
        if (e?.status == 409) {
          if (e?.error?.errors !== null) {
            for (const property in e.error.errors) {
              this.openAlert(e.error.errors[property]);
            }
          } else {
            this.openAlert(e?.error?.message);
          }
        }
      }
    });
  }

  discardChanges() {
    const groupId = this.f['groupId'].value;
    if (!groupId) return;
    this.appDataService.getGroupById(groupId).subscribe({
      next: (res: any) => {
        if (res?.data?.users) {
          this.selectedUsersList = res?.data?.users;
          this.f['userIds'].setValue(this.getIds(this.selectedUsersList));
        } else {
          this.selectedUsersList = [];
          this.f['userIds'].setValue([]);
        }
        this.displayUsers();
        this.hasUnsavedChanges = false;
        this.checkedAvailableIds = [];
        this.checkedAssignedIds = [];
      }
    });
  }

  openAlert(message: string) {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        message: message,
        buttonText: {
          cancel: 'OK'
        }
      },
    });
  }

  getUsers() {
    this.crudService.getAll(AppUrlConstants.USER_URL + AppUrlConstants.LIST).subscribe({
      next: (res: any) => {
        this.usersList = res?.data;
        this.displayUsers();
      },
      error: (e) => {}
    });
  }

  getAllGroups() {
    this.crudService.getAll(AppUrlConstants.GROUP_URL + AppUrlConstants.LIST).subscribe({
      next: (res: any) => {
        this.groupsList = res?.data;
        const index = this.groupsList.findIndex(group => group.name === 'Public User');
        if (index > -1) {
          this.groupsList.splice(index, 1);
        }
        this.filteredgroupsList = this.onFilteredListSearch(null, this.groupsList, 'name');
      }
    });
  }

  private getIds(list: any) {
    let ids: any = [];
    list?.forEach((i: any) => {
      ids.push(i?.userId);
    });
    return ids;
  }

  private displayUsers() {
    let users: any[] = [];
    const selectedUserIds = this.f['userIds']?.value || [];
    this.usersList?.forEach((u: any) => {
      if (!selectedUserIds.includes(u.userId)) {
        users.push(u);
      }
    });
    this.displayUsersList = users;
  }

  onFilteredListSearch(event: any, originalList: any[], property: string): any[] {
    let filteredList: any[];
    if (event && event.target.value) {
      filteredList = originalList.filter(item => item[property].toLowerCase().includes(event.target.value.toLowerCase()));
    } else {
      filteredList = originalList;
    }
    return filteredList;
  }
}
